import React, { useState, createContext, useMemo } from 'react';
import logComponent from '../common/logComponent';

// create context
const AdminContext = createContext(null);

const REFRESH_PERIOD = 600000; // 10 minutes

// declare provider component 
const AdminProvider = ({ children }) => {
  const [stagedOrganizations, setStagedOrganizations] = useState(null);
  const [stagedOrganizationsLastUpdate, setStagedOrganizationsLastUpdate] = useState(null);
  const [facilities, setFacilities] = useState(null);
  const [facilitiesLastUpdate, setFacilitiesLastUpdate] = useState(null);
  const [corporations, setCorporations] = useState(null);
  const [corporationsLastUpdate, setCorporationsLastUpdate] = useState(null);
  const [groups, setGroups] = useState(null);
  const [groupsLastUpdate, setGroupsLastUpdate] = useState(null);
  const [modules, setModules] = useState(null);
  const [modulesLastUpdate, setModulesLastUpdate] = useState(null);
  const [monitoredIndividuals, setMonitoredIndividuals] = useState(null);
  const [monitoredIndividualsLastUpdate, setMonitoredIndividualsLastUpdate] = useState(null);
  const [units, setUnits] = useState(null);
  const [unitsLastUpdate, setUnitsLastUpdate] = useState(null);
  const [shouldUpdateGroups, setShouldUpdateGroups] = useState(false);


  const setAllOrganizations = (organizations) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setAllOrganizations'); }

    setTheStagedOrganizations(organizations.stagedOrganizations);
    setTheCorporations(organizations.corporations);
    setTheFacilities(organizations.facilities);

  }

  const setTheStagedOrganizations = (organizations) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheStagedOrganizations'); }
    let newOrganizations = organizations ? [...organizations] : null;
    setStagedOrganizations(newOrganizations);
    setStagedOrganizationsLastUpdate(Date.now());
  }

  const removeStagedOrganization = (orgToRemove) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext removeStagedOrganization'); }
    const newStagedOrganizations = stagedOrganizations.filter(stagedOrg => {
      return !(
        (orgToRemove.stagedFacilityId && stagedOrg.stagedFacilityId === orgToRemove.stagedFacilityId) ||
        (orgToRemove.stagedCorporationId && stagedOrg.stagedCorporationId === orgToRemove.stagedCorporationId));
    })
    setStagedOrganizations(newStagedOrganizations);
  }

  const setTheFacilities = (facilities) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheFacilities'); }
    let newFacilities = facilities ? [...facilities] : null;
    setFacilities(newFacilities);
    setFacilitiesLastUpdate(Date.now());
  }

  const setTheCorporations = (corporations) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheCorporations'); }
    let newCorporations = corporations ? [...corporations] : null;
    setCorporations(newCorporations);
    setCorporationsLastUpdate(Date.now);
  }

  const setTheGroups = (groups) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheGroups'); }
    let newGroups = groups ? [...groups] : null;
    setGroups(newGroups);
    setGroupsLastUpdate(Date.now());
  }

  const setTheModules = (modules) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheModules'); }
    let newModules = modules ? [...modules] : null;
    setModules(newModules);
    setModulesLastUpdate(Date.now());
  }

  const setTheMonitoredIndividuals = (monitoredIndividualsToUpdate) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheMonitoredIndividuals'); }
    if (!(JSON.stringify(monitoredIndividuals) === JSON.stringify(monitoredIndividualsToUpdate))) {
      let newMonitoredIndividuals = monitoredIndividualsToUpdate ? [...monitoredIndividualsToUpdate] : null;
      setMonitoredIndividuals(newMonitoredIndividuals);
      setMonitoredIndividualsLastUpdate(Date.now());
    }

  }

  const setTheUnits = (unitsToUpate) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheUnits'); }

    if (!(JSON.stringify(units) === JSON.stringify(unitsToUpate))) {

      let newUnits = unitsToUpate ? [...unitsToUpate] : null;
      setUnits(newUnits);
      setUnitsLastUpdate(Date.now());
    }

  }

  const isFresh = (data) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext isFresh'); }
    switch (data) {
      case 'stagedOrganizations':
        return stagedOrganizationsLastUpdate &&
          stagedOrganizationsLastUpdate + REFRESH_PERIOD > Date.now();
      case 'corporations':
        return corporationsLastUpdate &&
          corporationsLastUpdate + REFRESH_PERIOD > Date.now();
      case 'facilities':
        return facilitiesLastUpdate &&
          facilitiesLastUpdate + REFRESH_PERIOD > Date.now();
      case 'groups':
        if (shouldUpdateGroups === true)
          return false
        else
          return groupsLastUpdate &&  
            groupsLastUpdate + REFRESH_PERIOD > Date.now();
      case 'modules':
        return modulesLastUpdate &&
          modulesLastUpdate + REFRESH_PERIOD > Date.now();
      case 'monitoredIndividuals':
        return monitoredIndividualsLastUpdate &&
          monitoredIndividualsLastUpdate + REFRESH_PERIOD > Date.now();
      case 'units':
        return units &&
          unitsLastUpdate + REFRESH_PERIOD > Date.now();
      default:
        return false;
    }
  }

  const setTheShouldUpdateGroups = (value) => {
    if (logComponent('AdminContext')) { console.debug('AdminContext setTheShouldUpdateGroups'); }
    setShouldUpdateGroups(!!value);

  }


  const adminState = useMemo(() => ({
    stagedOrganizations: stagedOrganizations,
    stagedOrganizationsLastUpdate: stagedOrganizationsLastUpdate,
    facilities: facilities,
    facilitiesLastUpdate: facilitiesLastUpdate,
    corporations: corporations,
    corporationsLastUpdate: corporationsLastUpdate,
    groups: groups,
    groupsLastUpdate: groupsLastUpdate,
    modules: modules,
    modulesLastUpdate: modulesLastUpdate,
    monitoredIndividuals: monitoredIndividuals,
    monitoredIndividualsLastUpdate: monitoredIndividualsLastUpdate,
    units: units,
    unitsLastUpdate: unitsLastUpdate,
    setAllOrganizations: setAllOrganizations,
    setStagedOrganizations: setTheStagedOrganizations,
    removeStagedOrganization: removeStagedOrganization,
    setFacilities: setTheFacilities,
    setCorporations: setTheCorporations,
    setGroups: setTheGroups,
    setModules: setTheModules,
    setMonitoredIndividuals: setTheMonitoredIndividuals,
    setUnits: setTheUnits,
    isFresh: isFresh,
    shouldUpdateGroups,
    setShouldUpdateGroups: setTheShouldUpdateGroups
  }), [
    stagedOrganizations,
    stagedOrganizationsLastUpdate,
    facilities,
    facilitiesLastUpdate,
    corporations,
    corporationsLastUpdate,
    groups,
    groupsLastUpdate,
    modules,
    modulesLastUpdate,
    monitoredIndividuals,
    monitoredIndividualsLastUpdate,
    units,
    unitsLastUpdate
  ]);

  return (
    <AdminContext.Provider value={adminState}>
      {children}
    </AdminContext.Provider>
  );
}

// export provider and context
export { AdminContext, AdminProvider };
